import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { theme } from '../theme';
import { getSavedPaymentMethod, getStripeConfig, purchasePinBundle } from '../api/atlast';
import CardFieldsGroup from '../CardFieldsGroup';

// PinBundlePurchaseModal — new (Aug 31, per Ali's report — Phase A of
// the new free/paid pin model build). Same structure and same two-path
// decision as the just-rebuilt FreezePaymentModal.js (confirm
// immediately if Stripe isn't configured or a saved card exists;
// otherwise collect a real card first) — genuinely modeled on it
// directly, not reinvented, since that's now the correct, working
// pattern for this app's Stripe checkout flows. 3 pins for $3 (Sep 9,
// per Ali's report — was $5), one-
// time, repeatable with no cap (see routes/pin_purchases.py).
function PinBundlePurchaseModal({ userId, currency = 'CAD', onSuccess, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [savedMethod, setSavedMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getStripeConfig();
        if (cancelled) return;
        if (config.publishable_key) {
          setStripeConfigured(true);
          setStripePromise(loadStripe(config.publishable_key));
          const saved = await getSavedPaymentMethod(userId).catch(() => null);
          if (!cancelled && saved?.has_saved_card) setSavedMethod(saved);
        }
      } catch (e) {
        console.error('PinBundlePurchaseModal: failed to load Stripe config', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const submitPurchase = async (paymentMethodId = null) => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await purchasePinBundle(userId, currency, paymentMethodId);
      onSuccess(result);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not complete this purchase. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={submitting ? undefined : onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',
          maxWidth: '420px', width: '100%', maxHeight: '90vh', overflowY: 'auto'
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: theme.textMuted }}>Loading…</p>
          </div>
        ) : (!stripeConfigured || savedMethod) ? (
          <div style={{ padding: '28px 24px' }}>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: '17px', fontWeight: 800, color: theme.textPrimary, marginBottom: '6px' }}>
              3 more pin slots
            </p>
            <p style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '18px' }}>
              Track 3 more trips right away. Each trip gets 25 checks, the same as your first three, and you can buy more anytime.
            </p>

            <div style={{ background: theme.bg, border: `2px solid ${theme.border}`, padding: '14px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: theme.textPrimary, fontWeight: 600 }}>
                {savedMethod
                  ? `${savedMethod.brand ? savedMethod.brand.charAt(0).toUpperCase() + savedMethod.brand.slice(1) : 'Card'} •••• ${savedMethod.last4}`
                  : 'No card saved yet'}
              </span>
              <span style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '15px', color: theme.structural }}>
                $3.00 USD
              </span>
            </div>

            {error && (
              <p style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '12px', fontWeight: 600 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => submitPurchase(null)}
                disabled={submitting}
                style={{
                  flex: 1, background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px',
                  padding: '13px', fontWeight: 800, fontSize: '14px', fontFamily: theme.fontDisplay,
                  cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1
                }}
              >
                {submitting ? 'Purchasing…' : 'Buy — $3.00 USD'}
              </button>
              <button
                onClick={onCancel}
                disabled={submitting}
                style={{
                  flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`, borderRadius: '0px',
                  padding: '13px', fontWeight: 700, fontSize: '14px', fontFamily: theme.fontDisplay,
                  cursor: submitting ? 'default' : 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <Elements stripe={stripePromise}>
            <NewCardPurchaseForm currency={currency} onSubmit={submitPurchase} onCancel={onCancel} error={error} submitting={submitting} />
          </Elements>
        )}
      </div>
    </div>
  );
}

export default PinBundlePurchaseModal;

function NewCardPurchaseForm({ currency, onSubmit, onCancel, error, submitting }) {
  const stripe = useStripe();
  const elements = useElements();
  const [localError, setLocalError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLocalError(null);

    const cardElement = elements.getElement(CardNumberElement);
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (pmError) {
      setLocalError(pmError.message || 'Could not process this card. Check the details and try again.');
      return;
    }

    onSubmit(paymentMethod.id);
  };

  return (
    <div style={{ padding: '28px 24px' }}>
      <p style={{ fontFamily: theme.fontDisplay, fontSize: '17px', fontWeight: 800, color: theme.textPrimary, marginBottom: '6px' }}>
        3 more pin slots — $3.00 USD
      </p>
      <p style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '18px' }}>
        Track 3 more trips right away. Each trip gets 25 checks, the same as your first three.
      </p>

      <CardFieldsGroup />

      {(localError || error) && (
        <p style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '12px', fontWeight: 600 }}>{localError || error}</p>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleSubmit}
          disabled={!stripe || submitting}
          style={{
            flex: 1, background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px',
            padding: '13px', fontWeight: 800, fontSize: '14px', fontFamily: theme.fontDisplay,
            cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1
          }}
        >
          {submitting ? 'Purchasing…' : 'Buy — $3.00 USD'}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`, borderRadius: '0px',
            padding: '13px', fontWeight: 700, fontSize: '14px', fontFamily: theme.fontDisplay,
            cursor: submitting ? 'default' : 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}